import { useCallback, useEffect, useRef } from 'react'
import { Keyboard } from 'react-native'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import { familyActions } from 'APP/Store/Family'
import { FormStep, AddDependentFormData } from '../types'
import { useAddDependent } from './provider'
import { checkDuplicateFamilyMembersHelper } from './helper'

export type UseAddDependentApiReturn = {
  adultInviteRunning: boolean
  requestError: { code?: string } | null
  checkDuplicateFamilyMembers: (email: string) => boolean | string
  sendEmailInvite: (email: string) => void
  onSubmit: () => Promise<void>
}

export const useAddDependentApi = (): UseAddDependentApiReturn => {
  const dispatch = useDispatch()
  const adultInviteRunning = useSelector((state) => state.family.requestRunning)
  const familyMembers = useSelector((state) => state.family.members)
  const requestError = useSelector((state) => state.family.requestError)
  const { addDependentForm } = useAddDependent()
  const { handleSubmit, setError, currentStep, setCurrentStep, isFormValid } = addDependentForm

  const previousErrorRef = useRef<{ code?: string } | null>(null)

  const checkDuplicateFamilyMembers = useCallback(
    (email: string): boolean | string => checkDuplicateFamilyMembersHelper(email, familyMembers),
    [familyMembers]
  )

  const sendEmailInvite = useCallback(
    (email: string) => {
      dispatch(familyActions.adultInviteRequest({ email }))
    },
    [dispatch]
  )

  const addChild = useCallback(
    (childProfile: { givenName: string; familyName: string; dateOfBirth: string }) => {
      dispatch(
        familyActions.recordChildConsentRequest({
          childData: childProfile,
        })
      )
    },
    [dispatch]
  )

  useEffect(() => {
    if (requestError && requestError !== previousErrorRef.current) {
      // Error is already handled by saga via handleNonRetryableRequestFailure
      // This effect is mainly for tracking
      previousErrorRef.current = requestError
    }
  }, [requestError])

  const onSubmit = useCallback(async () => {
    await handleSubmit((values: AddDependentFormData) => {
      if (currentStep === FormStep.INVITE_ADULT && values.email) {
        const email = values.email?.toLowerCase().trim()
        const duplicateCheck = checkDuplicateFamilyMembers(email)
        if (duplicateCheck !== true) {
          setError('email', {
            type: 'manual',
            message: duplicateCheck as string,
          })
          return
        }

        Keyboard.dismiss()
        sendEmailInvite(email)
        return
      }

      // If its the child flow and the form is valid, we need to show the consent form
      if (currentStep === FormStep.ADD_CHILD && isFormValid) {
        setCurrentStep(FormStep.GET_CONSENT)
        return
      }

      // This is when the consent form is shown and the user presses the continue button
      if (currentStep === FormStep.GET_CONSENT && isFormValid) {
        const { firstName, lastName, dateOfBirth } = values
        if (!firstName || !lastName || !dateOfBirth) {
          return
        }
        const childProfile = {
          givenName: firstName,
          familyName: lastName,
          dateOfBirth,
        }
        addChild(childProfile)
      }
    })()
  }, [
    currentStep,
    handleSubmit,
    checkDuplicateFamilyMembers,
    setError,
    sendEmailInvite,
    addChild,
    isFormValid,
    setCurrentStep,
  ])

  return {
    adultInviteRunning,
    requestError,
    checkDuplicateFamilyMembers,
    sendEmailInvite,
    onSubmit,
  }
}
